import { useForm, UseFormReturn } from 'react-hook-form';
import type { DefaultValues } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import type { IraUserCheckPayload, IraUserRequestForm } from 'services/requests';
import { useQueryParams } from 'hooks/routes';
import { useSubscribeUpdate } from 'hooks/common';
import {
  yupLastName,
  yupMiddleName,
  yupEmail,
  yupFirstName,
  yupEtzFee,
  yupCustodyFeePercentage,
  yupCustodyFeeAbsolute,
  yupNextBillingDate,
  yupCustodian,
  yupProfileType,
} from 'utils';
import { QueryKeys } from 'constants/queryKeys';
import { Parameters } from 'constants/queryParameters';
import { USER_STATUS } from 'constants/status';

export const useIraUserForm = (
  user?: IraUserCheckPayload | null,
): UseFormReturn<IraUserRequestForm> => {
  const schema: yup.SchemaOf<IraUserRequestForm> = yup.object({
    email: yupEmail,
    firstName: yupFirstName,
    middleName: yupMiddleName,
    lastName: yupLastName,
    etzFee: yupEtzFee,
    custodyFeePercentage: yupCustodyFeePercentage,
    custodyFeeAbsolute: yupCustodyFeeAbsolute,
    nextBillingDate: yupNextBillingDate,
    custodianType: yupCustodian,
    profileType: yupProfileType,
  });

  const queryClient = useQueryClient();
  const id = useQueryParams(Parameters.iraUserId);
  const queryKey = [QueryKeys.iraUser, id];
  const defaultValues: DefaultValues<IraUserRequestForm> =
    queryClient.getQueryState<IraUserRequestForm>(queryKey)?.data ?? {
      firstName: user?.firstName,
      middleName: user?.middleName,
      lastName: user?.lastName,
      email: user?.email,
      custodianType: user?.custodianType,
      profileType: user?.profileType,
      etzFee: user?.etzFee,
      nextBillingDate: user?.nextBillingDate,
      custodyFeePercentage:
        user?.custodyFeePercentage !== undefined
          ? String(user.custodyFeePercentage)
          : undefined,
      custodyFeeAbsolute:
        user?.custodyFeeAbsolute !== undefined
          ? String(user.custodyFeeAbsolute)
          : undefined,
    };

  const form = useForm<IraUserRequestForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(schema),
    defaultValues,
    context: { isActive: user?.profileStatus === USER_STATUS.active },
  });

  useSubscribeUpdate(queryKey, (data?: IraUserRequestForm) => {
    if (data) {
      form.reset({
        ...data,
        nextBillingDate: data.nextBillingDate ? data.nextBillingDate : '',
        custodyFeeAbsolute: data.custodyFeeAbsolute
          ? String(data.custodyFeeAbsolute)
          : '',
        custodyFeePercentage: data.custodyFeePercentage
          ? String(data.custodyFeePercentage)
          : '',
      });
    }
  });

  return form;
};
